import React, { FC, useEffect, useState } from 'react';
import { Grid, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { FinderButton, FinderType } from '@/components/Finder/FinderButton';
import { MediaResourceType } from '@/declarations/models/MediaResourceType';
import { Credit, M24MediaInfo, M24MediaModel } from '@/declarations/models/M24MediaModel';
import { useStore } from '@/components/store/Store';
import { Api } from '@/services/Api';
import { Tag } from '@/declarations/models/Tag';
import { TagType } from '@/declarations/models/TagType';
import { MediaLicense } from '@/declarations/models/MediaLicense';
import { isDeepEqual } from '@/utils/object';
import { User } from '@/declarations/models/User';
import MediaEditorHeader from './MediaEditorHeader';
import EditMediaLicenseCreditsFields from './MediaEditorFields/EditMediaLicenseCreditsFields';
import Styles from '../../assets/js/Styles';
import EditMediaTagsField from './MediaEditorFields/EditMediaTagsField';
import { EditMediaTextFields, inputGroupStyling } from './MediaEditorFields/EditMediaTextFields';
import MediaEditorPreview from './MediaEditorPreview';
import Container from '../../components/Container';

export interface MediaEditorProps {
  resourceId: number;
  onCloseEditor: () => void;
}

const editorStyling = {
  height: '100%',
  overflowY: 'auto',
  gridGap: 4,
  padding: 2,
  borderRadius: 2,
  backgroundColor: Styles.Colors.FINDER_M24_PRIMARY_HEADER_BACKGROUND,
};

export const MediaEditor: FC<MediaEditorProps> = ({ resourceId, onCloseEditor }) => {
  const { state } = useStore();
  const { t: tCommon } = useTranslation('common');
  const { t: tComponents } = useTranslation('components');
  const { enqueueSnackbar } = useSnackbar();
  const siteId = state.selectedSite?.id;
  const [previewOpen, setPreviewOpen] = useState<boolean>(true);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const [savedMedia, setSavedMedia] = useState<M24MediaModel>();
  const [allTags, setAllTags] = useState<Array<Tag>>([]);
  const [uploadedBy] = useState<User>();

  const [modifiedMedia, setModifiedMedia] = useState<M24MediaModel>();

  useEffect(() => {
    if (resourceId && siteId) {
      const mediaCtx = Api.getM24MediaResource(siteId, resourceId);
      const tagCtx = Api.getAllTagsForSite(siteId, { type: TagType.TAG });
      mediaCtx
        .fetchDirect(null)
        .catch((e) => console.error(e))
        .then((mediaRes) => {
          if (mediaRes) {
            setSavedMedia(mediaRes);
            // TODO: Include user when retrieving media
            // Api.getUser(mediaRes.created_by_id)
            //   .fetchDirect(null)
            //   .then((user) => user && setUploadedBy(user));
          }
        })
        .then(() =>
          tagCtx
            .fetchDirect([])
            .then((tagsRes) => tagsRes?.length && setAllTags(tagsRes))
            .finally(tagCtx.abort),
        )
        .finally(mediaCtx.abort);
    }
  }, [resourceId, siteId]);

  useEffect(() => setModifiedMedia(savedMedia), [savedMedia]);

  useEffect(() => setIsDirty(!isDeepEqual(savedMedia, modifiedMedia)), [modifiedMedia, savedMedia]);

  const handleSaveMedia = async () => {
    if (siteId && modifiedMedia) {
      const ctx = Api.createM24MediaResource(siteId, modifiedMedia);
      await ctx
        .fetchDirect(null)
        .then((res) => {
          if (res) {
            setSavedMedia(res);
            enqueueSnackbar(tCommon('saveSuccess'), { variant: 'success' });
          } else {
            // fetchDirect returns null (defaultValue) on non-2xx responses without throwing,
            // so the error snackbar must be shown here rather than in .catch
            enqueueSnackbar(tCommon('saveFailed', { error: 'Unknown error' }), { variant: 'error' });
          }
        })
        .catch((e) => enqueueSnackbar(tCommon('saveFailed', { error: e.error || e }), { variant: 'error' }))
        .finally(ctx.abort);
    }
  };

  const handleTitleChange = (title: string, locale?: string) => {
    if (modifiedMedia) {
      if (!locale) {
        setModifiedMedia({
          ...modifiedMedia,
          content: {
            ...modifiedMedia?.content,
            title: title || undefined,
          },
          title: title || undefined,
        } as M24MediaModel);
      } else {
        setModifiedMedia({
          ...modifiedMedia,
          content: {
            ...modifiedMedia?.content,
            [locale]: {
              ...(modifiedMedia?.content?.[locale] as M24MediaInfo | undefined),
              title: title || undefined,
            },
          },
        });
      }
    }
  };

  const handleTextFieldChange = (field: 'caption' | 'alttext', value: string, locale?: string) => {
    if (modifiedMedia) {
      if (!locale) {
        setModifiedMedia({
          ...modifiedMedia,
          content: {
            ...modifiedMedia?.content,
            [field]: value || undefined,
          },
        } as M24MediaModel);
      } else {
        setModifiedMedia({
          ...modifiedMedia,
          content: {
            ...modifiedMedia?.content,
            [locale]: {
              ...(modifiedMedia?.content?.[locale] as M24MediaInfo | undefined),
              [field]: value || undefined,
            },
          },
        });
      }
    }
  };

  const handleLicenseChange = (license: MediaLicense) => {
    if (modifiedMedia) {
      setModifiedMedia({
        ...modifiedMedia,
        content: {
          ...modifiedMedia?.content,
          license_code: license || undefined,
        },
      } as M24MediaModel);
    }
  };

  const handleCreditsChange = (credits: Array<Credit>) => {
    if (modifiedMedia) {
      setModifiedMedia({
        ...modifiedMedia,
        content: {
          ...modifiedMedia?.content,
          credits: credits.length ? credits : undefined,
        },
      } as M24MediaModel);
    }
  };

  const handleTagsChange = (tags: Array<string>) => {
    if (modifiedMedia) {
      setModifiedMedia({
        ...modifiedMedia,
        tags: tags.length ? tags.map((tag) => ({ tag })) : undefined,
      });
    }
  };

  function onChangePoster(source: M24MediaModel) {
    if (modifiedMedia) {
      setModifiedMedia({
        ...modifiedMedia,
        content: {
          ...modifiedMedia.content,
          poster: source,
        },
      } as M24MediaModel);
    }
  }
  return (
    <Container fullHeight fullWidth column gap={0}>
      <MediaEditorHeader
        mediaItem={savedMedia}
        previewOpen={previewOpen}
        setPreviewOpen={() => setPreviewOpen(!previewOpen)}
        onClose={onCloseEditor}
        onSave={handleSaveMedia}
        isDirty={isDirty}
      />
      <Grid container sx={{ marginTop: '16px', height: 'calc(100% -  56px)', width: '100%' }}>
        <Grid size={previewOpen ? 6 : 12} sx={editorStyling}>
          <Container fullWidth fullHeight column top gap={2}>
            <EditMediaTextFields
              item={modifiedMedia}
              setTitle={handleTitleChange}
              setCaption={(value, locale) => handleTextFieldChange('caption', value, locale)}
              setAltText={(value, locale) => handleTextFieldChange('alttext', value, locale)}
              locales={state.selectedSite?.site_contents}
            />
            <EditMediaLicenseCreditsFields
              credits={modifiedMedia?.content?.credits}
              setCredits={handleCreditsChange}
              license={modifiedMedia?.content?.license_code}
              setLicense={handleLicenseChange}
            />
            <EditMediaTagsField
              allTags={allTags}
              tagsInput={modifiedMedia?.tags?.map((tag) => tag.tag)}
              setTagsInput={handleTagsChange}
            />

            {(modifiedMedia?.type === MediaResourceType.DOCUMENT ||
              modifiedMedia?.type === MediaResourceType.AUDIO ||
              modifiedMedia?.type === MediaResourceType.VIDEO) && (
              <Container left mt={2} fullWidth top>
                <Container left column gap={2} sx={{ maxWidth: 400 }}>
                  <FinderButton
                    type={FinderType.M24}
                    overrideText={tComponents('MediaEditor.pickPoster')}
                    multiSelect={false}
                    finderProps={{
                      selectableResourceTypes: [MediaResourceType.AUDIO],
                      onSelectionConfirmed: (selected) => {
                        onChangePoster(selected[0].source);
                      },
                      defaultResourceType: MediaResourceType.IMAGE,
                    }}
                  />

                  {modifiedMedia?.content?.poster && (
                    // Remove poster
                    <Button
                      variant='contained'
                      color='error'
                      onClick={() =>
                        setModifiedMedia({
                          ...modifiedMedia,
                          content: {
                            ...modifiedMedia.content,
                            poster: undefined,
                          },
                        } as M24MediaModel)
                      }>
                      {tComponents('MediaEditor.removePoster')}
                    </Button>
                  )}
                </Container>

                {modifiedMedia?.content?.poster && (
                  <Container left column gap={1}>
                    <img
                      src={modifiedMedia.content.poster.url}
                      alt='Poster Preview'
                      style={{ maxWidth: '200px', maxHeight: '200px' }}
                    />
                  </Container>
                )}
              </Container>
            )}

            {modifiedMedia?.dmsf_id && (
              <Container left sx={inputGroupStyling} column>
                <Typography variant='h6'>{tComponents('MediaEditor.DAMS')}</Typography>
                <Typography variant='caption'>{tComponents('MediaEditor.DAMSInfo')}</Typography>

                <Container left top gap={4} sx={{ mt: 1 }} wrap>
                  {modifiedMedia?.content?.dams_data?.producer &&
                    modifiedMedia?.content?.dams_data?.producer.length > 0 && (
                      <Container left column gap={1}>
                        <Typography variant='overline'>{tComponents('MediaEditor.DAMSProducers')}</Typography>
                        {modifiedMedia.content.dams_data.producer.map((producer) => (
                          <Typography key={producer.unique_id} variant='body2'>
                            {producer.title}
                          </Typography>
                        ))}
                      </Container>
                    )}

                  {modifiedMedia?.content?.dams_data?.subjects &&
                    modifiedMedia?.content?.dams_data?.subjects.length > 0 && (
                      <Container left column gap={1}>
                        <Typography variant='overline'>{tComponents('MediaEditor.DAMSSubjects')}</Typography>
                        {modifiedMedia.content.dams_data.subjects.map((subject) => (
                          <Typography key={subject.unique_id} variant='body2'>
                            {subject.title}
                          </Typography>
                        ))}
                      </Container>
                    )}

                  {modifiedMedia?.content?.dams_data?.places &&
                    modifiedMedia?.content?.dams_data?.places.length > 0 && (
                      <Container left column gap={1}>
                        <Typography variant='overline'>{tComponents('MediaEditor.DAMSPlaces')}</Typography>
                        {modifiedMedia.content.dams_data.places.map((place) => (
                          <Typography key={place.unique_id} variant='body2'>
                            {place.title}
                          </Typography>
                        ))}
                      </Container>
                    )}

                  {modifiedMedia?.content?.dams_data?.licenses &&
                    modifiedMedia?.content?.dams_data?.licenses.length > 0 && (
                      <Container left column gap={1}>
                        <Typography variant='overline'>{tComponents('MediaEditor.DAMSLicenses')}</Typography>
                        {modifiedMedia.content.dams_data.licenses.map((license) => (
                          <Typography key={license.unique_id} variant='body2'>
                            {license.title}
                          </Typography>
                        ))}
                      </Container>
                    )}
                </Container>
              </Container>
            )}
          </Container>
        </Grid>
        {previewOpen && (
          <Grid size={6} sx={{ height: '100%' }}>
            <MediaEditorPreview media={modifiedMedia} uploadedBy={uploadedBy} siteId={siteId} />
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default MediaEditor;
